import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { postureContent } from '../data/mock';
import AnimatedSection from '../components/common/AnimatedSection';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Eye, HelpCircle, Lightbulb, Zap, 
  Target, Users, Trophy, Check
} from 'lucide-react';

const PosturePage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const content = postureContent[language] || postureContent.fr;

  const verbIcons = [Eye, HelpCircle, Lightbulb, Zap];
  const iconColors = ['bg-[#5B2D8E]', 'bg-[#7C4DAF]', 'bg-[#FFC107]', 'bg-[#0D9488]'];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="min-h-[70vh] flex items-center bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#FFC107]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <h1 className="heading-1 text-white mb-8">
                {content.hero.title}
              </h1>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <p className="text-xl lg:text-2xl text-white/80 mb-12 max-w-3xl mx-auto">
                {content.hero.subtitle}
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/rejoindre" className="btn-secondary">
                  Je rejoins l'ADA
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/propositions" 
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-bold rounded-full border border-white/30 hover:bg-white/20 transition-all"
                >
                  Voir nos propositions
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Ce que ça veut dire */}
      <section className={`section-lg ${isDark ? 'bg-[#1C1917]' : 'bg-white'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <h2 className="heading-2 text-center mb-12">{content.concrete.title}</h2>
              <div className="divider mx-auto mb-12" />
            </AnimatedSection>

            <div className="space-y-4">
              {content.concrete.items.map((item, index) => (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <motion.div 
                    className={`flex items-center gap-4 rounded-2xl p-5 border ${
                      isDark 
                        ? 'bg-[#292524] border-white/10' 
                        : 'bg-[#FAFAF9] border-stone-100'
                    }`}
                    whileHover={{ x: 8 }}
                  >
                    <div className="w-10 h-10 bg-[#5B2D8E] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-lg font-medium text-theme-primary">
                      {item}
                    </p>
                  </motion.div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Méthode - 4 verbes */}
      <section className={`section-lg ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <AnimatedSection className="text-center mb-16">
            <span className="caption mb-4 block">{content.method.title}</span>
            <h2 className="heading-2 mb-4">{content.method.subtitle}</h2>
            <div className="divider mx-auto" />
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {content.method.verbs.map((verb, index) => {
              const Icon = verbIcons[index];
              return (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <motion.div 
                    className="card-feature text-center group"
                    whileHover={{ y: -8 }}
                  >
                    <div className={`w-16 h-16 mx-auto mb-5 ${iconColors[index]} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-8 h-8 ${index === 2 ? 'text-[#1C1917]' : 'text-white'}`} />
                    </div>
                    <h3 className="heading-3 mb-3">{verb.word}</h3>
                    <p className="text-theme-secondary">{verb.description}</p>
                  </motion.div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trajectoire */}
      <section className={`section-lg ${isDark ? 'bg-[#1C1917]' : 'bg-white'}`}>
        <div className="container-custom">
          <AnimatedSection className="text-center mb-16">
            <span className="caption mb-4 block">{content.trajectory.title}</span>
            <div className="divider mx-auto" />
          </AnimatedSection>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {content.trajectory.steps.map((step, index) => {
                const stepIcons = [Target, Users, Trophy];
                const Icon = stepIcons[index];
                return (
                  <AnimatedSection key={index} delay={index * 0.15}>
                    <motion.div 
                      className="text-center"
                      whileHover={{ scale: 1.05 }}
                    >
                      <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF] rounded-full flex items-center justify-center shadow-lg">
                        <Icon className="w-10 h-10 text-white" />
                      </div>
                      <div className="text-sm font-bold text-[#FFC107] mb-2">Étape {index + 1}</div>
                      <h3 className="heading-3 mb-3">{step.title}</h3>
                      <p className="text-theme-secondary">{step.description}</p>
                    </motion.div>
                  </AnimatedSection>
                );
              })}
            </div>

            <AnimatedSection delay={0.5} className="mt-12 text-center">
              <p className="text-xl font-semibold" style={{ color: isDark ? '#FFC107' : '#5B2D8E' }}>
                {content.trajectory.quote}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#FFC107]/20 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <p className="handwritten text-3xl text-[#FFC107] mb-6">{content.signature.punchline}</p>
              <h2 className="heading-2 text-white mb-10">
                {language === 'fr' ? 'Prêt à construire avec nous ?' : 'Ready to build with us?'}
              </h2>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/rejoindre" className="btn-secondary">
                  Rejoindre l'ADA
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/rejoindre#microdon" 
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-bold rounded-full border border-white/30 hover:bg-white/20 transition-all"
                >
                  Faire un micro-don
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PosturePage;
